import { Modal, SubmitButton } from "@xola/ui-kit";
import { FormField, InputField, requiredString } from "common";
import { FormikProps } from "formik";
import React, { useRef } from "react";
import { Except } from "type-fest";
import { Factor, UserType } from "types";
import { object } from "yup";
import { useUser } from "../../../hooks/useAccount";
import { getAuthenticationFactor, getDefaultAuthenticationFactor } from "../../../models/User";
import { SecondaryButton } from "../../Common/SecondaryButton";
import { XolaFormik } from "../../Common/XolaFormik";

export enum AuthenticationFor {
    MFA_SETTING = "mfa_setting",
    CHANGE_PASSWORD = "change_password",
    CHANGE_BANK_DETAILS = "change_bank_details",
    CHANGE_EMAIL = "change_email",
}

const validationSchema = object().shape({
    code: requiredString(),
});

export interface MultiFactorAuthenticationModalProps {
    authenticationFor?: AuthenticationFor;
    isOpen?: boolean;
    isSaving?: boolean;
    factorId?: string;
    phoneNo?: string;
    onSuccess: (code: string) => void;
    onClose: () => void;
    factor?: Factor;
}

interface FormValues {
    code: string;
}

const initialValues: FormValues = { code: "" };

export const MultiFactorAuthenticationModal = ({
    authenticationFor,
    isOpen = false,
    isSaving = false,
    factorId,
    phoneNo,
    onSuccess,
    onClose,
    factor,
}: MultiFactorAuthenticationModalProps) => {
    return (
        <Modal isOpen={isOpen} size="medium" onClose={onClose}>
            <ModalContent
                authenticationFor={authenticationFor}
                factorId={factorId}
                phoneNo={phoneNo}
                isSaving={isSaving}
                factor={factor}
                onClose={onClose}
                onSuccess={onSuccess}
            />
        </Modal>
    );
};

const ModalContent = ({
    authenticationFor,
    factorId,
    phoneNo,
    isSaving,
    onSuccess,
    onClose,
    factor = undefined,
}: Except<MultiFactorAuthenticationModalProps, "isOpen">) => {
    const user = useUser();
    const forSellerAdminBankChange =
        user.type !== UserType.SELLER && authenticationFor === AuthenticationFor.CHANGE_BANK_DETAILS;
    const multiFactorAuthenticationDefaultFactor = getDefaultAuthenticationFactor(user);
    const defaultFactorID = factorId ?? multiFactorAuthenticationDefaultFactor?.id;
    const authenticationFactor = getAuthenticationFactor(user, defaultFactorID);
    const profileInfoFormRef = useRef<FormikProps<FormValues>>(null);

    const forMFASetting = authenticationFor === AuthenticationFor.MFA_SETTING;
    const maskedPhoneNumber = forSellerAdminBankChange
        ? getMaskedNumber(factor?.phone)
        : getMaskedNumber(authenticationFactor?.phone ?? phoneNo);

    const isDefaultFactor = factorId === multiFactorAuthenticationDefaultFactor?.id;
    const title = forMFASetting ? `Verify ${isDefaultFactor ? "Old" : "New"} Number` : "Verify Code";
    const phoneDetail = `${isDefaultFactor ? "old" : "new"} phone number ${maskedPhoneNumber}`;

    const handleSubmit = async (values: FormValues) => {
        onSuccess(values.code);
    };

    return (
        <XolaFormik
            enableReinitialize
            validationSchema={validationSchema}
            initialValues={initialValues}
            innerRef={profileInfoFormRef}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit }) => (
                <>
                    <Modal.Header>{title}</Modal.Header>
                    <div className="mt-4 text-center text-gray-darker">
                        <span>Enter the authentication code sent to </span>
                        <span>
                            {forSellerAdminBankChange ? (
                                <span>
                                    <b>primary account owner&apos;s</b> MFA number{" "}
                                </span>
                            ) : (
                                "your "
                            )}
                        </span>
                        <span className="font-bold">
                            <var data-var="phoneNumber">{forMFASetting ? phoneDetail : maskedPhoneNumber}</var>
                        </span>
                    </div>

                    <Modal.Body>
                        <FormField name="code" label="Code">
                            <InputField />
                        </FormField>
                    </Modal.Body>

                    <Modal.Footer className="space-x-4">
                        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                        <SubmitButton isLoading={isSaving} type="submit" color="primary" onClick={handleSubmit}>
                            Confirm
                        </SubmitButton>
                    </Modal.Footer>
                </>
            )}
        </XolaFormik>
    );
};

const getMaskedNumber = (phoneNumber?: string) => {
    return phoneNumber?.slice(-4).padStart(6, "*");
};
